/**
 * InitialsAvatar — deterministic, colourful "initials tile" fallback
 * used everywhere we would otherwise show a gray silhouette. Male
 * members get a navy-blue gradient, female members get a rose-pink
 * gradient, and unknown/other genders get a saffron gradient drawn
 * from the name hash so every card has a consistent tone.
 *
 * Usage:
 *   <InitialsAvatar name="Kailash Menon" gender="male" size={72} />
 *
 * Or, drop-in with an <img>-like signature via ``<PhotoOrInitials>``:
 *   <PhotoOrInitials src={m.photo} name={m.first_name} gender={m.gender} size={72} />
 */
import React from "react";

// Curated gradient palettes. Male/female get a fixed pair so a whole
// tree looks colour-coded (blue/pink) the way the sample-tree
// illustration does. "Unknown" gender rotates through a warm saffron
// palette so single-parent nodes still look inviting.
const UNKNOWN_GRADIENTS = [
  ["#F97316", "#FDBA74"], // orange
  ["#B45309", "#FBBF24"], // amber
  ["#C2410C", "#F97316"], // deep orange
  ["#9A3412", "#FB923C"], // burnt-orange
  ["#7C2D12", "#EA580C"], // dark saffron
  ["#B91C1C", "#F87171"], // maroon-rose
];

function hashCode(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0; // int32
  }
  return Math.abs(h);
}

export function nameToGradient(name = "", gender = "") {
  const g = String(gender || "").toLowerCase();
  if (g === "female" || g === "f" || g === "woman") return ["#DB2777", "#F472B6"];
  if (g === "male"   || g === "m" || g === "man")   return ["#1E40AF", "#60A5FA"];
  const idx = hashCode(name) % UNKNOWN_GRADIENTS.length;
  return UNKNOWN_GRADIENTS[idx];
}

// Common honorifics/prefixes we skip so "Dr Kailash Menon" → "KM"
// instead of "DM". Kept lowercase, no punctuation for a cheap check.
const TITLES = new Set([
  "dr", "dr.", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "mx", "mx.",
  "sri", "shri", "smt", "smt.", "kum", "kumari",
  "prof", "prof.", "professor", "sir", "madam", "shrimati",
  "capt", "capt.", "col", "col.", "gen", "gen.", "lt", "lt.",
  "rev", "rev.", "fr", "fr.", "pastor",
  "the", "hon", "hon.", "honorable", "honourable",
]);

export function getInitials(name = "") {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((p) => !TITLES.has(p.toLowerCase()));
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function InitialsAvatar({
  name = "",
  gender = "",
  size = 48,
  className = "",
  square = false,
  fill = false,
  style = {},
  title,
}) {
  const [c1, c2] = nameToGradient(name, gender);
  const initials = getInitials(name);
  // Slightly larger initials than a naive size*0.4 so the tile reads
  // even at small sizes (mobile grids).
  const fontSize = fill
    ? "clamp(1.75rem, 6vw, 3.5rem)"
    : Math.max(11, Math.round(size * 0.42));
  const sizeStyle = fill
    ? { width: "100%", height: "100%" }
    : { width: size, height: size };
  return (
    <div
      className={`inline-flex items-center justify-center font-semibold text-white select-none ${square ? "rounded-2xl" : "rounded-full"} ${className}`}
      style={{
        ...sizeStyle,
        fontSize,
        background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
        letterSpacing: "0.02em",
        textShadow: "0 1px 2px rgba(0,0,0,0.15)",
        ...style,
      }}
      title={title || name}
      aria-label={`Initials avatar for ${name || "unknown"}`}
      data-testid="initials-avatar"
    >
      {initials}
    </div>
  );
}

/**
 * Photo-or-initials — renders the src as an <img> when present,
 * falls back to a colourful initials tile otherwise. When the image
 * fails to load, we swap to initials too (via onError → hide img).
 */
export function PhotoOrInitials({
  src,
  name,
  gender,
  size = 48,
  className = "",
  square = false,
  alt,
  title,
  style = {},
}) {
  const [broken, setBroken] = React.useState(false);
  const useInitials = !src || broken;

  if (useInitials) {
    return (
      <InitialsAvatar
        name={name}
        gender={gender}
        size={size}
        square={square}
        className={className}
        style={style}
        title={title}
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt || name || ""}
      onError={() => setBroken(true)}
      className={`object-cover ${square ? "rounded-2xl" : "rounded-full"} ${className}`}
      style={{ width: size, height: size, ...style }}
      title={title}
      data-testid="photo-or-initials-img"
    />
  );
}
